import { inject, Injectable } from '@angular/core';
import { concatMap, Observable } from 'rxjs';
import { Interceptor } from './interceptors-types';
import { InitThreadResponse, PromptResponse, SuggestionsResponse } from '../endpoint-contracts';
import {
    INIT_THREAD_RESPONSE_INTERCEPTORS,
    PROMPT_REQUEST_INTERCEPTORS,
    PROMPT_RESPONSE_INTERCEPTORS,
    STREAMING_PROMPT_RESPONSE_INTERCEPTORS,
    SUGGESTIONS_RESPONSE_INTERCEPTORS
} from './ai-chat-prompt-interceptor.provider';

@Injectable({ providedIn: 'root' })
export class AiChatInterceptorsService {
    private beforeRequestInterceptors: Interceptor<string>[] = inject(PROMPT_REQUEST_INTERCEPTORS, { optional: true }) || [];
    private promptResponseInterceptors: Interceptor<PromptResponse>[] = inject(PROMPT_RESPONSE_INTERCEPTORS, { optional: true }) || [];
    private streamingPromptResponseInterceptors: Interceptor<PromptResponse>[] =
        inject(STREAMING_PROMPT_RESPONSE_INTERCEPTORS, { optional: true }) || [];
    private initThreadResponseInterceptors: Interceptor<InitThreadResponse>[] =
        inject(INIT_THREAD_RESPONSE_INTERCEPTORS, { optional: true }) || [];
    private suggestionsResponseInterceptors: Interceptor<SuggestionsResponse>[] =
        inject(SUGGESTIONS_RESPONSE_INTERCEPTORS, { optional: true }) || [];

    public runBeforeRequest(initialRequest$: Observable<string>): Observable<string> {
        return this.applyInterceptors<string>(initialRequest$, this.beforeRequestInterceptors);
    }

    public runAfterResponse(initialResponse$: Observable<PromptResponse>): Observable<PromptResponse> {
        return this.applyInterceptors<PromptResponse>(initialResponse$, this.promptResponseInterceptors);
    }

    public runStreamingInterceptors(initialResponse$: Observable<PromptResponse>): Observable<PromptResponse> {
        return this.applyInterceptors<PromptResponse>(initialResponse$, this.streamingPromptResponseInterceptors);
    }

    public runInitThreadResponseInterceptors(initialResponse$: Observable<InitThreadResponse>): Observable<InitThreadResponse> {
        return this.applyInterceptors<InitThreadResponse>(initialResponse$, this.initThreadResponseInterceptors);
    }

    public runSuggestionsResponseInterceptors(initialResponse$: Observable<SuggestionsResponse>): Observable<SuggestionsResponse> {
        return this.applyInterceptors<SuggestionsResponse>(initialResponse$, this.suggestionsResponseInterceptors);
    }

    private applyInterceptors<T>(initial$: Observable<T>, interceptors: Interceptor<T>[]): Observable<T> {
        let current$ = initial$;
        for (const interceptor of interceptors) {
            current$ = current$.pipe(concatMap((value: T) => interceptor(value)));
        }
        return current$;
    }
}
